import { useCallback, useState } from 'react';
import { showToast } from '@attentive-platform/stem-ui';

import { FormContainer } from '../../styled';
import { isValidEmail } from '../../Utils/HelperFunctions';
import { fetchApiData, interpolate } from '../../Utils/ApiCalls';
import { ENTERPRISE_CALLBACKS, ENTERPRISE_CALLBACK_DETAIL } from '../../urls';

import CallbacksList from './components/CallbacksList';
import CallbackSearchSection from './components/CallbackSearchSection';
import { mapFromApi, buildApiPayload } from './callbackHelpers';

export const EnterpriseCallbackManager = () => {
  const [email, setEmail] = useState('');
  const [searchedEmail, setSearchedEmail] = useState('');
  const [callbacks, setCallbacks] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const updateCallback = (localId, patch) => {
    setCallbacks(prev => prev.map(c => (c.localId === localId ? { ...c, ...patch } : c)));
  };

  const loadCallbacks = useCallback(async userEmail => {
    const { data, error } = await fetchApiData({
      endpoint: ENTERPRISE_CALLBACKS,
      apiRequest: 'GET',
      params: { email: userEmail }
    });
    if (error) {
      showToast(typeof error === 'string' ? error : 'Failed to load callbacks', 'error');
      setCallbacks([]);
      return;
    }
    const list = Array.isArray(data) ? data : [];
    setCallbacks(list.map(mapFromApi));
  }, []);

  const handleSearch = async () => {
    const trimmed = email.trim();
    if (!trimmed || !isValidEmail(trimmed)) {
      showToast('Enter a valid email', 'error');
      return;
    }
    setSearchLoading(true);
    const { data, error } = await fetchApiData({
      endpoint: ENTERPRISE_CALLBACKS,
      apiRequest: 'GET',
      params: { email: trimmed }
    });
    setSearchLoading(false);
    if (error) {
      showToast(typeof error === 'string' ? error : 'Search failed', 'error');
      setSearchedEmail('');
      setCallbacks([]);
      return;
    }
    setSearchedEmail(trimmed);
    const list = Array.isArray(data) ? data : [];
    setCallbacks(list.map(mapFromApi));
  };

  const handleAdd = () => {
    if (!searchedEmail) return;
    const n = callbacks.length + 1;
    setCallbacks(prev => [
      ...prev,
      {
        localId: `new-${Date.now()}`,
        id: null,
        name: `Callback ${n}`,
        callbackTargetType: 1,
        verb: 1,
        url: '',
        events: [],
        callbackRequireData: false,
        headers: []
      }
    ]);
  };

  const handleSave = async c => {
    if (!searchedEmail) return;
    if (c.verb == null || !c.url?.trim() || !c.events?.length) {
      showToast('Verb, URL, and at least one event are required', 'error');
      return;
    }
    setSavingId(c.localId);
    const isNew = c.id == null;
    const payload = buildApiPayload(c);
    const body = isNew ? { ...payload, email: searchedEmail } : payload;
    const { error } = isNew
      ? await fetchApiData({ endpoint: ENTERPRISE_CALLBACKS, apiRequest: 'POST', data: body })
      : await fetchApiData({
          endpoint: interpolate(ENTERPRISE_CALLBACK_DETAIL, [c.id]),
          apiRequest: 'PATCH',
          data: body
        });
    setSavingId(null);
    if (error) {
      showToast(typeof error === 'string' ? error : 'Save failed', 'error');
      return;
    }
    showToast('Saved', 'success');
    await loadCallbacks(searchedEmail);
  };

  const handleDelete = async c => {
    if (!searchedEmail) return;
    if (c.id == null) {
      setCallbacks(prev => prev.filter(x => x.localId !== c.localId));
      return;
    }
    setDeletingId(c.localId);
    const { error } = await fetchApiData({
      endpoint: interpolate(ENTERPRISE_CALLBACK_DETAIL, [c.id]),
      apiRequest: 'DELETE'
    });
    setDeletingId(null);
    if (error) {
      showToast(typeof error === 'string' ? error : 'Delete failed', 'error');
      return;
    }
    showToast('Callback deleted', 'success');
    await loadCallbacks(searchedEmail);
  };

  const addHeaderRow = localId => {
    setCallbacks(prev =>
      prev.map(c =>
        c.localId === localId ? { ...c, headers: [...c.headers, { key: '', value: '' }] } : c
      )
    );
  };

  const updateHeader = (localId, index, field, value) => {
    setCallbacks(prev =>
      prev.map(c =>
        c.localId === localId
          ? { ...c, headers: c.headers.map((h, i) => (i === index ? { ...h, [field]: value } : h)) }
          : c
      )
    );
  };

  const removeHeader = (localId, index) => {
    setCallbacks(prev =>
      prev.map(c =>
        c.localId === localId ? { ...c, headers: c.headers.filter((_, i) => i !== index) } : c
      )
    );
  };

  return (
    <FormContainer>
      <CallbackSearchSection
        email={email}
        setEmail={setEmail}
        onSearch={handleSearch}
        onAdd={handleAdd}
        searchLoading={searchLoading}
        canAdd={!!searchedEmail}
      />

      {searchedEmail && (
        <CallbacksList
          callbacks={callbacks}
          savingId={savingId}
          deletingId={deletingId}
          onUpdate={updateCallback}
          onDelete={handleDelete}
          onSave={handleSave}
          onAddHeader={addHeaderRow}
          onUpdateHeader={updateHeader}
          onRemoveHeader={removeHeader}
        />
      )}
    </FormContainer>
  );
};

export default EnterpriseCallbackManager;
